import { NavLink, Outlet, useLocation } from "react-router-dom";
import { AppWindow, BookOpen, FileText, LayoutList, LibraryBig, FlaskConical, FolderKanban, Hash, Server, ShieldCheck, SlidersHorizontal } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@lumanity/ui/primitives";
import { useProjectServiceUser } from "@lumanity/ui/projects";
import PageLayout from "@/components/PageLayout";
import { cn } from "@/lib/utils";

const navGroups = [
  {
    heading: "Projects",
    items: [
      { path: "projects", label: "All Projects", description: "Every PubCreator project; open or edit its setup", icon: FolderKanban },
      {
        path: "project-availability",
        label: "Project Availability",
        description: "Choose which apps each project is available in",
        icon: AppWindow,
      },
    ],
  },
  {
    heading: "Project Setup",
    items: [
      { path: "therapy-areas", label: "Therapy Areas", description: "Therapy areas offered on the project form", icon: FlaskConical },
      { path: "project-codes", label: "Project Codes", description: "Project codes offered on the project form", icon: Hash },
      {
        path: "supporting-document-types",
        label: "Supporting Document Types",
        description: "Kinds of project reference document (SOW, style guide, lexicon, …)",
        icon: FileText,
      },
      {
        path: "ai-permissions",
        label: "AI Permissions",
        description: "Statements a project creator confirms before AI is used on the project",
        icon: ShieldCheck,
      },
      {
        path: "project-properties",
        label: "Project Properties",
        description: "PubCreator's own project fields, such as the Model dropdown",
        icon: SlidersHorizontal,
      },
    ],
  },
  {
    heading: "PubCreator",
    items: [
      {
        path: "target-outlets",
        label: "Target Outlets",
        description: "Journals and congresses writers can target, with their submission guidelines",
        icon: BookOpen,
      },
      {
        path: "outline-templates",
        label: "Outline Templates",
        description: "The sections each output type's First draft starts from",
        icon: LayoutList,
      },
      {
        path: "prompt-library",
        label: "Prompt Library",
        description: "The prompt library writers choose a section's AI instructions from",
        icon: LibraryBig,
      },
    ],
  },
  {
    heading: "Platform",
    items: [
      {
        path: "registered-apps",
        label: "Registered Apps",
        description: "Register apps with the project service and choose the optional project sections each one shows",
        icon: Server,
        platformAdminOnly: true,
      },
    ],
  },
];

const navItems = navGroups.flatMap((g) => g.items);

const Admin = () => {
  const { pathname } = useLocation();
  const current = navItems.find((n) => pathname.endsWith(n.path)) ?? navItems[0];
  // Platform-admin pages stay reachable by URL (they show a notice), but only platform admins see them listed.
  const { data: serviceUser } = useProjectServiceUser();
  const visibleGroups = navGroups
    .map((g) => ({ ...g, items: g.items.filter((item) => !("platformAdminOnly" in item) || serviceUser?.is_platform_admin) }))
    .filter((g) => g.items.length > 0);

  return (
    <PageLayout>
      <div className="flex gap-6">
        <aside className="w-60 shrink-0 flex flex-col gap-1">
          {visibleGroups.map(({ heading, items }, i) => (
            <div key={heading} className={cn("flex flex-col gap-1", i > 0 && "mt-4")}>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-2">{heading}</p>
              {items.map(({ path, label, icon: Icon }) => (
                <NavLink
                  key={path}
                  to={path}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    )
                  }
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  {label}
                </NavLink>
              ))}
            </div>
          ))}
        </aside>

        <div className="flex-1 min-w-0">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>{current.label}</CardTitle>
              <CardDescription>{current.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <Outlet />
            </CardContent>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
};

export default Admin;
